import type { Track } from '@/lib/db';
import {
  encodeTrackFile,
  requestPersistentStorage,
  TrackFileRepository,
  TrackRepository,
} from '@/lib/db';
import type { NormalisedTrack } from '@/lib/parse';

import { checkForDuplicate } from './duplicates';
import type { DuplicateVerdict } from './duplicates';

/**
 * Writing a parsed track to the database, once.
 *
 * Kept apart from parsing because the two fail for unrelated reasons and a
 * pilot needs to be told which: "this file is not a track I can read" is a
 * problem with the file, and "this flight is already here" is not a problem at
 * all. Parsing is also pure and runs in a worker; this touches IndexedDB and
 * cannot.
 */

export type ImportOutcome = 'imported' | 'skipped-duplicate' | 'failed';

export interface ImportResult {
  outcome: ImportOutcome;
  track?: Track;
  duplicate?: DuplicateVerdict;
  message?: string;
  /** Technical detail, shown only when a row is expanded. */
  detail?: string[];
}

/** The file a track was parsed from, kept alongside it — see `TrackFile`. */
export interface OriginalFile {
  filename: string;
  bytes: Uint8Array;
}

export interface ImportOptions {
  repository?: TrackRepository;
  fileRepository?: TrackFileRepository;
  /** Absent for a track fetched from a network source, which has no file. */
  original?: OriginalFile;
  /**
   * Ask the browser to make storage non-evictable on the first successful
   * write. Off in tests, where there is no browser to ask.
   */
  requestPersistence?: boolean;
}

let persistenceRequested = false;

/** Reset the once-per-session persistence flag. Tests only. */
export function resetPersistenceRequest(): void {
  persistenceRequested = false;
}

export async function importNormalisedTrack(
  normalised: NormalisedTrack,
  options: ImportOptions = {},
): Promise<ImportResult> {
  const repository = options.repository ?? new TrackRepository();
  const fileRepository = options.fileRepository ?? new TrackFileRepository();
  const { original } = options;

  /*
   * A duplicate track is still a file the pilot handed over. The same flight
   * as a CSV after its KML parses to the same points, and the CSV is skipped
   * as a track — but it is kept, against the track it duplicates, so nothing
   * dropped on this app is ever quietly thrown away. Best-effort: the track
   * is already safe, and failing to store a second copy of its source must
   * not turn "already imported" into an error.
   */
  const attachTo = async (existing: Track | undefined): Promise<void> => {
    if (original === undefined || existing === undefined) {
      return;
    }
    try {
      await fileRepository.attach(
        encodeTrackFile({
          trackId: existing.id,
          filename: original.filename,
          bytes: original.bytes,
        }),
      );
    } catch {
      // Swallowed by design — see above.
    }
  };

  const duplicate = await checkForDuplicate(normalised.track, repository);
  if (duplicate.kind === 'identical') {
    await attachTo(duplicate.existing);
    return {
      outcome: 'skipped-duplicate',
      duplicate,
      ...(duplicate.message === undefined ? {} : { message: duplicate.message }),
      ...(duplicate.existing === undefined ? {} : { track: duplicate.existing }),
    };
  }

  /*
   * The first write is the right moment to ask for persistent storage, and the
   * reason `requestPersistentStorage` has existed since Phase 0 with nothing
   * calling it. Firefox and Safari show a prompt, and a prompt on an empty app
   * is a prompt about nothing; a pilot who has just imported a flight knows
   * exactly what is being kept.
   */
  if (options.requestPersistence === true && !persistenceRequested) {
    persistenceRequested = true;
    await requestPersistentStorage();
  }

  try {
    const files =
      original === undefined
        ? []
        : [
            encodeTrackFile({
              trackId: normalised.track.id,
              filename: original.filename,
              bytes: original.bytes,
              addedAt: normalised.track.importedAt,
            }),
          ];
    await repository.add(normalised.track, normalised.points, files);
  } catch (cause) {
    /*
     * `tracks.contentHash` is a unique index, so two identical files dropped in
     * the same batch can both pass the duplicate check and then collide on
     * write. That is the index doing its job, not an error worth showing as
     * one — the second file is a duplicate, which is what it is reported as.
     */
    const existing = await repository.findByContentHash(normalised.track.contentHash);
    if (existing !== undefined) {
      await attachTo(existing);
      return {
        outcome: 'skipped-duplicate',
        track: existing,
        duplicate: { kind: 'identical', existing },
        message: 'The same fixes arrived twice in this batch; the second copy was skipped.',
      };
    }
    return {
      outcome: 'failed',
      message: 'This track could not be saved to the local database.',
      detail: [cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause)],
    };
  }

  return {
    outcome: 'imported',
    track: normalised.track,
    duplicate,
    ...(duplicate.kind === 'overlapping' && duplicate.message !== undefined
      ? { message: duplicate.message }
      : {}),
  };
}
